// @flow
import React, { useEffect, useState } from 'react';
import { camelizeKeys } from 'humps';
// Components
import VisibilitySensor from 'react-visibility-sensor';
import { Link } from 'react-router-dom';
import {
  ProgressiveImageLoader,
  scaleConverter,
  priceConverter,
} from '../../common';
import { AddToCartButton } from '../../current-order';
// Styles
import './SearchResultSquare.css';
// Types
import type { Hit } from '../types';
// Analytics
import * as Analytics from '../../common/analytics';

type Props = {
  hit: Hit,
};

const SearchResultSquare = ({ hit }: Props) => {
  const camelizedHit: Hit = camelizeKeys(hit);
  const {
    id,
    slug,
    name,
    price,
    listPrice,
    manufacturer,
    unitForQuantity,
    images,
    defaultQuantity,
    incrementQuantity,
    inStock,
    isGiftCard,
    cookingRecommendations,
  } = camelizedHit;
  // Typesense always returns the document id as a string, but line items reference variants by numeric id
  const productId = Number(id);
  const [productVisibility, setProductVisibility] = useState(false);

  useEffect(() => {
    if (productVisibility) {
      Analytics.logProductImpression(
        camelizedHit,
        window.location.pathname,
        'search result square',
      );
    }
    // eslint-disable-next-line
  }, [productVisibility]);

  const calculateProductDiscount = (listPrice, price) => {
    if (listPrice > price) {
      const discountPercentage = ((listPrice - price) / listPrice) * 100;
      return Math.round(discountPercentage);
    }
    return null;
  };

  const productOnPromotion = listPrice > price;
  const productDiscount = calculateProductDiscount(listPrice, price);

  const convertedPrice = scaleConverter(price, unitForQuantity);
  const convertedListPrice = scaleConverter(listPrice, unitForQuantity);

  return (
    <VisibilitySensor
      onChange={isVisible => {
        setProductVisibility(isVisible);
      }}
    >
      <div className="search-result-square-main">
        <section className="search-result-square-image-section">
          <Link className="search-result-square-link" to={`/produtos/${slug}/`}>
            {productOnPromotion && productDiscount >= 5 && (
              <div className="product-square-image-section-discount">
                <p>{Math.round(productDiscount)}%</p>
                <span>OFF</span>
              </div>
            )}
            <ProgressiveImageLoader
              selectedImage={images[0]}
              imageToShow={'product'}
              disabled={!inStock}
            />
          </Link>
        </section>

        <section className="search-result-square-info-section">
          <p className="search-result-square-variant">{manufacturer}</p>
          <Link className="search-result-square-link" to={`/produtos/${slug}/`}>
            <h3 title={name} className="search-result-square-title">
              {name}
            </h3>
          </Link>
        </section>
        {/* Commented for possible future implementation */}
        {/* <div className="card-cooking-recomedation-box-container">
            <div className="card-cooking-recomedation-box-title">{cookingRecommendations.length > 0 && 'Ideal Para'}</div>
            <div className="card-cooking-recomedation-box-wrapper">
              {cookingRecommendations.map((item, i) => (
                <div key={i} className="card-cooking-recomedation-box-item">
                  <img src={item.smallUrl} alt={item.name} />
                  <p>{item.name}</p>
                </div>
              ))}
            </div>
          </div> */}

        <div className={`card-product-price-main`}>
          <div
            className={`card-product-price-main-titles ${
              productOnPromotion ? 'card-product-price-promotion' : ''
            }`}
          >
            <h5>{priceConverter(convertedPrice)}</h5>
            {productOnPromotion && (
              <h6>{priceConverter(convertedListPrice)}</h6>
            )}
          </div>
          <p>
            Preço /
            {unitForQuantity === 'g' || unitForQuantity === 'kg' ? 'Kg' : 'un'}
          </p>
        </div>

        <section>
          <div className="search-result-square-button">
            {isGiftCard ? (
              <Link
                className="search-result-square-link"
                to={`/produtos/${slug}/`}
              >
                <AddToCartButton
                  productId={productId}
                  unitForQuantity={unitForQuantity}
                  defaultQuantity={defaultQuantity}
                  incrementQuantity={incrementQuantity}
                  inStock={inStock}
                  largeSize={true}
                  disable={true}
                  // USE ANALYTICS
                  analyticsEventType={'impression'}
                  analyticsViewSource={'search result square'}
                />
              </Link>
            ) : (
              <AddToCartButton
                productId={productId}
                unitForQuantity={unitForQuantity}
                defaultQuantity={defaultQuantity}
                incrementQuantity={incrementQuantity}
                inStock={inStock}
                largeSize={true}
                // USE ANALYTICS
                analyticsEventType={'impression'}
                analyticsViewSource={'search result square'}
              />
            )}
          </div>
        </section>
      </div>
    </VisibilitySensor>
  );
};

export default SearchResultSquare;
